import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { getSupabaseClient } from '../supabaseClient';
import { useBusinessType } from '../context/BusinessContext';
import { 
  Buildings, 
  CircleNotch, 
  ArrowSquareOut, 
  Warning,
  Check, 
  Info, 
  X,
  Gear,
  Plus,
  PencilSimple,
  Trash,
  MapPin,
  PencilSimple as Edit2,
  FloppyDisk,
  Spinner,
  Truck,
  ShirtFolded,
  Sparkle,
  Stack,
  Copy
} from '@phosphor-icons/react';
import { toast } from '../components/ui/sonner-toast';
import { motion, AnimatePresence } from 'framer-motion';
import PageHeader from '../components/PageHeader';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useMediaQuery } from '../hooks/use-media-query';

const CurbsideIntegration = ({ 
  inModal = false, 
  connectionId = null,
  onConfigSaved = null
}) => {
  const { businessType } = useBusinessType();
  const supabase = getSupabaseClient(businessType);
  const navigate = useNavigate();
  const location = useLocation();
  
  // State for form inputs
  const [url, setUrl] = useState('');
  const [signupUrl, setSignupUrl] = useState('');
  const [username, setUsername] = useState('bella@thesavants.ai'); // Fixed username
  const [password, setPassword] = useState('BellaAI1!'); // Fixed password
  const [serviceOptions, setServiceOptions] = useState([]);
  const [serviceTypes, setServiceTypes] = useState('laundry');
  const [bellaCreated, setBellaCreated] = useState(false); // New field for tracking if Bella employee was created
  
  // State for loading, errors, and settings ID
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [urlError, setUrlError] = useState(null);
  const [curbsideSettingsId, setCurbsideSettingsId] = useState(null);
  const [showSetupInstructions, setShowSetupInstructions] = useState(false);
  const [userData, setUserData] = useState(null);
  const [integrationStatus, setIntegrationStatus] = useState('not_started');
  const [aiPhoneNumber, setAiPhoneNumber] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const [totalSteps] = useState(4);
  
  // Add validation state
  const [formIsValid, setFormIsValid] = useState(false);

  // Missing state variables that were causing the error
  const [routes, setRoutes] = useState([]);
  const [zones, setZones] = useState([]);
  const [settings, setSettings] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [editingType, setEditingType] = useState(null);

  // Mobile responsive hook
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Tab state - read from URL parameter, but force setup if integration not complete
  const [activeTab, setActiveTab] = useState(() => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get('tab') || 'setup';
  });

  // Force activeTab to 'setup' when integration is not complete
  useEffect(() => {
    if (integrationStatus !== 'complete' && activeTab !== 'setup') {
      setActiveTab('setup');
    }
  }, [integrationStatus, activeTab]);

  // Animation variants
  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1],
        staggerChildren: 0.15
      }
    }
  };

  const cardVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.2,
        ease: [0, 0, 0.2, 1]
      }
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  // Validate form whenever url or bellaCreated changes (including on initial load)
  useEffect(() => {
    const validation = validateSubdomain(url);
    const isUrlValid = !validation && url.trim() !== '';
    const isValid = isUrlValid && bellaCreated;
    setFormIsValid(isValid);
  }, [url, bellaCreated]);

  // Validate Curbside subdomain input with helpful error messages
  const validateSubdomain = (input) => {
    if (!input || input.trim() === '') {
      return null; // No error for empty input
    }

    const trimmedInput = input.trim();
    
    // Check if they entered a full URL
    if (trimmedInput.includes('http://') || trimmedInput.includes('https://')) {
      const urlMatch = trimmedInput.match(/https?:\/\/([^-]+)/);
      if (urlMatch) {
        const extractedSubdomain = urlMatch[1];
        return {
          message: `You entered a full website address. Just enter "${extractedSubdomain}" (the part before -pds.curbsidelaundries.com).`,
          suggestion: extractedSubdomain
        };
      }
      return {
        message: "Don't enter the full website address. Just enter your subdomain (the part before -pds.curbsidelaundries.com).",
        suggestion: null
      };
    }
    
    // Check if they included the full domain
    if (trimmedInput.includes('curbsidelaundries.com')) {
      const subdomainMatch = trimmedInput.match(/([^\.]+)/);
      if (subdomainMatch) {
        const extractedSubdomain = subdomainMatch[1].replace('-pds', '');
        return {
          message: `You entered the full domain. Just enter "${extractedSubdomain}" (your subdomain only).`,
          suggestion: extractedSubdomain
        };
      }
      return {
        message: "Don't include 'curbsidelaundries.com'. Just enter your subdomain.",
        suggestion: null
      };
    }
    
    // Check if they included -pds
    if (trimmedInput.includes('-pds')) {
      const cleanSubdomain = trimmedInput.replace('-pds', '');
      return {
        message: `Remove "-pds" from your entry. Just enter "${cleanSubdomain}".`,
        suggestion: cleanSubdomain
      };
    }
    
    // Check for spaces
    if (trimmedInput.includes(' ')) {
      const suggestion = trimmedInput.replace(/\s+/g, '').toLowerCase();
      return {
        message: `Remove all spaces and use lowercase letters. Try "${suggestion}" instead.`,
        suggestion: suggestion
      };
    }
    
    // Check for dashes
    if (trimmedInput.includes('-')) {
      const suggestion = trimmedInput.replace(/-/g, '').toLowerCase();
      return {
        message: `Remove all dashes and use lowercase letters. Try "${suggestion}" instead.`,
        suggestion: suggestion
      };
    }
    
    // Check for underscores
    if (trimmedInput.includes('_')) {
      const suggestion = trimmedInput.replace(/_/g, '').toLowerCase();
      return {
        message: `Remove all underscores and use lowercase letters. Try "${suggestion}" instead.`,
        suggestion: suggestion
      };
    }
    
    // Check for dots
    if (trimmedInput.includes('.')) {
      const suggestion = trimmedInput.replace(/\./g, '').toLowerCase();
      return {
        message: `Remove all dots and use lowercase letters. Try "${suggestion}" instead.`,
        suggestion: suggestion
      };
    }
    
    // Check for uppercase letters
    if (trimmedInput !== trimmedInput.toLowerCase()) {
      const suggestion = trimmedInput.toLowerCase();
      return {
        message: `Use only lowercase letters. Try "${suggestion}" instead.`,
        suggestion: suggestion
      };
    }
    
    // Check for special characters
    if (!/^[a-z0-9]+$/.test(trimmedInput)) {
      const suggestion = trimmedInput.replace(/[^a-z0-9]/gi, '').toLowerCase();
      if (suggestion) {
        return {
          message: `Use only letters and numbers (no special characters). Try "${suggestion}" instead.`,
          suggestion: suggestion
        };
      } else {
        return {
          message: "Use only letters and numbers. For example, if your business is 'Wash Works', enter 'washworks'.",
          suggestion: null
        };
      }
    }
    
    return null; // Input is valid
  };

  // Handle input change - simplified without auto-save
  const handleInputChange = (field, value) => {
    let newUrl = url;
    let newBellaCreated = bellaCreated;
    
    // Update the specific field state
    switch (field) {
      case 'url':
        newUrl = value;
        setUrl(value);
        // Validate the subdomain input
        const validation = validateSubdomain(value);
        setUrlError(validation);
        break;
      case 'signupUrl':
        setSignupUrl(value);
        break;
      case 'username':
        // Username is now fixed, but keep for compatibility
        setUsername('bella@thesavants.ai');
        break;
      case 'password':
        // Password is now fixed, but keep for compatibility
        setPassword('BellaAI1!');
        break;
      case 'serviceOptions':
        setServiceOptions(value);
        break;
      case 'serviceTypes':
        setServiceTypes(value);
        break;
      case 'bellaCreated':
        newBellaCreated = value;
        setBellaCreated(value);
        break;
      default:
        break;
    }
    
    // Check form validity using the latest values (not state which might be stale)
    const validation = validateSubdomain(newUrl);
    const isUrlValid = !validation && newUrl.trim() !== '';
    
    const isValid = 
      isUrlValid && 
      newBellaCreated; // Only need URL and Bella created confirmation
    
    setFormIsValid(isValid);
  };

  // Function to apply suggested correction
  const applySuggestion = (suggestion) => {
    setUrl(suggestion);
    setUrlError(null);
    
    // Recheck form validity with the corrected URL
    const isValid = 
      suggestion.trim() !== '' && 
      username.trim() !== '' && 
      password.trim() !== '';
    
    setFormIsValid(isValid);
  };

  // Fetch existing settings
  const fetchSettings = async () => {
    try {
      setLoading(true);
      
      // Get account ID using the same utility as other components
      const { getUserAccountId } = await import('../utils/accountUtils');
      const { accountId, error: accountIdError } = await getUserAccountId();
      if (accountIdError || !accountId) throw new Error(accountIdError || 'Account ID not found');
      
      // Get current user for userData state
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      
      if (user) {
        setUserData(user);
        
        // Check for existing settings
        const { data, error } = await supabase
          .from('curbside_settings')
          .select('*')
          .eq('account_id', accountId)
          .maybeSingle();
          
        if (error && error.code !== 'PGRST116') throw error;
        
        if (data) {
          // Set form fields with existing data
          setUrl(data.url || '');
          setSignupUrl(data.signup_url || '');
          setUsername('bella@thesavants.ai'); // Always use fixed username
          setPassword('BellaAI1!'); // Always use fixed password
          setServiceOptions(Array.isArray(data.service_options) ? data.service_options : []);
          setServiceTypes(data.service_types || 'laundry');
          setBellaCreated(data.bella_created || false); // Load Bella created status
          setCurbsideSettingsId(data.id);
          setIntegrationStatus(data.integration_status || 'not_started');
        }
      }
    } catch (err) {
      console.error('Error fetching Curbside settings:', err);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  // Send setup request to API
  const handleSetupCurbside = async () => {
    try {
      setLoading(true);
      
      // Get account ID using the same utility as other components
      const { getUserAccountId } = await import('../utils/accountUtils');
      const { accountId, error: accountIdError } = await getUserAccountId();
      if (accountIdError || !accountId) throw new Error(accountIdError || 'Account ID not found');
      
      // Variable to track the current settings ID (from state or newly created)
      let currentSettingsId = curbsideSettingsId;
      
      // First, ensure a row exists in the curbside_settings table
      if (!currentSettingsId) {
        // Get current user data if we don't have it
        if (!userData) {
          const { data: { user }, error: userError } = await supabase.auth.getUser();
          if (userError) throw userError;
          setUserData(user);
        }
        
        // Create a new record with current form values (even if empty)
        const { data, error } = await supabase
          .from('curbside_settings')
          .insert({
            account_id: accountId,
            url: url.trim() || '',
            signup_url: signupUrl.trim() || '',
            username: 'bella@thesavants.ai', // Fixed username
            password: 'BellaAI1!', // Fixed password
            service_options: Array.isArray(serviceOptions) ? serviceOptions : [],
            service_types: serviceTypes || 'laundry',
            bella_created: bellaCreated || false, // New field
            integration_status: 'not_started',
            updated_at: new Date().toISOString()
          })
          .select()
          .single();
          
        if (error) throw error;
        
        // Store the ID locally and also update the state
        currentSettingsId = data.id;
        setCurbsideSettingsId(data.id);
      } else {
        // Update the existing record with current form values
        const { error } = await supabase
          .from('curbside_settings')
          .update({
            url: url.trim() || '',
            signup_url: signupUrl.trim() || '',
            username: 'bella@thesavants.ai', // Fixed username
            password: 'BellaAI1!', // Fixed password
            service_options: Array.isArray(serviceOptions) ? serviceOptions : [],
            service_types: serviceTypes || 'laundry',
            bella_created: bellaCreated || false, // New field
            updated_at: new Date().toISOString()
          })
          .eq('id', currentSettingsId);
          
        if (error) throw error;
      }
      
      // Validate required fields now that we have a row
      if (!url.trim() || !bellaCreated) {
        toast.error("Please complete all required fields first");
        setLoading(false);
        return;
      }
      
      // If we have a connectionId, also create a connections_config entry
      if (connectionId) {
        try {
          const { error: configError } = await supabase
            .from('connections_config')
            .insert({
              connection_id: connectionId,
              account_id: accountId,
              config: {
                url: url.trim(),
                username: username.trim(),
                password: password.trim(),
                signupUrl: signupUrl.trim() || '',
                serviceTypes: serviceTypes || 'laundry'
              }
            });
            
          if (configError) {
            console.error('Error creating connection config:', configError);
          } else {
            console.log('Connection config created successfully');
            
            // If in modal, call the onConfigSaved callback
            if (inModal && onConfigSaved) {
              toast.success("Curbside configuration saved successfully");
              onConfigSaved();
              return; // Exit early to avoid page reload
            }
          }
        } catch (configErr) {
          console.error('Error in connection config:', configErr);
        }
      }
      
      // Update the integration status to in_progress - using the local ID variable
      const { error: updateError } = await supabase
        .from('curbside_settings')
        .update({ integration_status: 'in_progress' })
        .eq('id', currentSettingsId);
        
      if (updateError) throw updateError;
      
      // Update local state
      setIntegrationStatus('in_progress');
      
      // Make API request to setup Curbside
      try {
        const response = await fetch('https://cleaner-automation-936dbbf97f9e.herokuapp.com/api/browser/curbside/setup', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            accountId: accountId
          })
        });
        
        if (!response.ok) {
          console.error('API setup request failed but continuing with refresh');
        } else {
          toast.success("Curbside integration setup started successfully");
        }
      } catch (apiError) {
        console.error('API request error:', apiError);
        toast.error("Setup initiated but API request failed. The page will refresh.");
      }
      
      // Only reload the page if not in modal
      if (!inModal) {
        // Always refresh the page after a brief delay, regardless of API success
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
      
    } catch (err) {
      console.error('Error setting up Curbside:', err);
      setError(err.message || 'Failed to setup Curbside integration');
      toast.error(err.message || 'Failed to setup Curbside integration');
      
      // Only reload the page if not in modal
      if (!inModal) {
        // Still refresh after error (after user sees the error message)
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch AI phone number from vapi_call_settings
  const fetchAiPhoneNumber = async () => {
    try {
      const { getUserAccountId } = await import('../utils/accountUtils');
      const { accountId, error: accountIdError } = await getUserAccountId();
      if (accountIdError || !accountId) return;
      
      const { data, error } = await supabase
        .from('vapi_call_settings')
        .select('phone_number')
        .eq('account_id', accountId)
        .maybeSingle();
        
      if (error) throw error;
      setAiPhoneNumber(data?.phone_number || 'Your AI phone number');
    } catch (err) {
      console.error('Error fetching AI phone number:', err);
      setAiPhoneNumber('Your AI phone number');
    }
  };

  useEffect(() => {
    if (userData && showSetupInstructions) {
      fetchAiPhoneNumber();
    }
  }, [userData, showSetupInstructions]);

  // Handle Bella creation confirmation
  const handleBellaCreatedConfirmation = async () => {
    try {
      setBellaCreated(true);
      
      const { getUserAccountId } = await import('../utils/accountUtils');
      const { accountId, error: accountIdError } = await getUserAccountId();
      if (accountIdError || !accountId) throw new Error(accountIdError || 'Account ID not found');
      
      // Update database
      const { error } = await supabase
        .from('curbside_settings')
        .update({ 
          bella_created: true,
          updated_at: new Date().toISOString()
        })
        .eq('account_id', accountId);

      if (error) throw error;
      
      toast.success("Bella employee creation confirmed!");
      setShowSetupInstructions(false);
      setCurrentStep(1);
    } catch (err) {
      console.error('Error updating Bella created status:', err);
      toast.error('Failed to update status');
    }
  };

  // Handle service types change with immediate database update
  const handleServiceTypesChange = async (value) => {
    try {
      setServiceTypes(value);
      
      const { getUserAccountId } = await import('../utils/accountUtils');
      const { accountId, error: accountIdError } = await getUserAccountId();
      if (accountIdError || !accountId) return;
      
      // Update database immediately
      const { error } = await supabase
        .from('curbside_settings')
        .update({ 
          service_types: value,
          updated_at: new Date().toISOString()
        })
        .eq('account_id', accountId);

      if (error) throw error;
      
      toast.success("Service types updated successfully!");
    } catch (err) {
      console.error('Error updating service types:', err);
      toast.error('Failed to update service types');
    }
  };

  // Format phone number from E164 to (xxx) xxx-xxxx format
  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return 'Your AI phone number';
    
    // Remove any non-digit characters
    const cleaned = phoneNumber.replace(/\D/g, '');
    
    // Check if it's a valid US phone number (10 or 11 digits)
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      // Remove the leading 1 for US numbers
      const number = cleaned.slice(1);
      return `(${number.slice(0, 3)}) ${number.slice(3, 6)}-${number.slice(6)}`;
    } else if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    
    // If it doesn't match expected format, return original
    return phoneNumber;
  };

  // Get raw phone number digits for copying
  const getRawPhoneDigits = (phoneNumber) => {
    if (!phoneNumber) return '';
    
    // Remove any non-digit characters
    const cleaned = phoneNumber.replace(/\D/g, '');
    
    // If it's 11 digits starting with 1, remove the leading 1
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return cleaned.slice(1);
    }
    
    // Return 10 digit number
    return cleaned.length === 10 ? cleaned : phoneNumber;
  };



  // Fetch all data
  const fetchData = async () => {
    try {
      setLoading(true);
      
      const { getUserAccountId } = await import('../utils/accountUtils');
      const { accountId, error: accountIdError } = await getUserAccountId();
      if (accountIdError || !accountId) throw new Error(accountIdError || 'Account ID not found');
      
      // Fetch settings
      const { data: settingsData, error: settingsError } = await supabase
        .from('curbside_settings')
        .select('*')
        .eq('account_id', accountId)
        .maybeSingle();
      
      if (settingsError && settingsError.code !== 'PGRST116') throw settingsError;
      setSettings(settingsData);
      setServiceOptions(settingsData?.service_options || []);
      setServiceTypes(settingsData?.service_types || 'laundry');

      // Fetch routes
      const { data: routesData, error: routesError } = await supabase
        .from('curbside_route_settings')
        .select('*')
        .eq('account_id', accountId);
      
      if (routesError) throw routesError;
      setRoutes(routesData || []);

      // Fetch zones
      const { data: zonesData, error: zonesError } = await supabase
        .from('curbside_zone_settings')
        .select('*')
        .eq('account_id', accountId);
      
      if (zonesError) throw zonesError;
      console.log('Zones loaded:', zonesData); // Debug logging
      setZones(zonesData || []);

    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userData) {
      fetchData();
    }
  }, [userData]);

  // Delete functions
  const deleteRoute = async (id) => {
    try {
      const { error } = await supabase
        .from('curbside_route_settings')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      toast.success('Route deleted successfully');
      fetchData();
    } catch (err) {
      console.error('Error deleting route:', err);
      toast.error('Failed to delete route');
    }
  };

  const deleteZone = async (id) => {
    try {
      const { error } = await supabase
        .from('curbside_zone_settings')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      toast.success('Zone deleted successfully');
      fetchData();
    } catch (err) {
      console.error('Error deleting zone:', err);
      toast.error('Failed to delete zone');
    }
  };

  // Save functions
  const saveRoute = async (routeData) => {
    try {
      const { getUserAccountId } = await import('../utils/accountUtils');
      const { accountId, error: accountIdError } = await getUserAccountId();
      if (accountIdError || !accountId) throw new Error(accountIdError || 'Account ID not found');
      
      // Sanitize route data with proper defaults
      const data = {
        route_name: routeData.route_name || '',
        wash_and_fold_turnaround: routeData.wash_and_fold_turnaround || null,
        dry_cleaning_turnaround: routeData.dry_cleaning_turnaround || null,
        days: Array.isArray(routeData.days) ? routeData.days : [],
        times: Array.isArray(routeData.times) ? routeData.times : [],
        special_weekend_hours: routeData.special_weekend_hours || null,
        account_id: accountId,
        updated_at: new Date().toISOString()
      };

      if (routeData.id) {
        data.id = routeData.id;
        const { error } = await supabase
          .from('curbside_route_settings')
          .update(data)
          .eq('id', routeData.id);
        if (error) throw error;
      } else {
        data.created_at = new Date().toISOString();
        const { error } = await supabase
          .from('curbside_route_settings')
          .insert(data);
        if (error) throw error;
      }

      toast.success('Route saved successfully');
      setEditingItem(null);
      setEditingType(null);
      fetchData();
    } catch (err) {
      console.error('Error saving route:', err);
      toast.error('Failed to save route');
    }
  };

  const saveZone = async (zoneData) => {
    try {
      const { getUserAccountId } = await import('../utils/accountUtils');
      const { accountId, error: accountIdError } = await getUserAccountId();
      if (accountIdError || !accountId) throw new Error(accountIdError || 'Account ID not found');
      
      // Sanitize zone data with proper defaults
      const data = {
        zone_name: zoneData.zone_name || '',
        price_per_pound_laundry: parseFloat(zoneData.price_per_pound_laundry) || null,
        price_add_per_item: parseFloat(zoneData.price_add_per_item) || null,
        price_add_dry_cleaning: parseFloat(zoneData.price_add_dry_cleaning) || null,
        order_minimum: parseFloat(zoneData.order_minimum) || null,
        service_areas: Array.isArray(zoneData.service_areas) ? zoneData.service_areas : [],
        route_id: zoneData.route_id || null,
        account_id: accountId,
        updated_at: new Date().toISOString()
      };

      if (zoneData.id) {
        data.id = zoneData.id;
        const { error } = await supabase
          .from('curbside_zone_settings')
          .update(data)
          .eq('id', zoneData.id);
        if (error) throw error;
      } else {
        data.created_at = new Date().toISOString();
        const { error } = await supabase
          .from('curbside_zone_settings')
          .insert(data);
        if (error) throw error;
      }

      toast.success('Zone saved successfully');
      setEditingItem(null);
      setEditingType(null);
      fetchData();
    } catch (err) {
      console.error('Error saving zone:', err);
      toast.error('Failed to save zone');
    }
  };

  const saveServiceOption = async (serviceOptionData) => {
    try {
      // Sanitize service option data
      const sanitizedOption = {
        item: serviceOptionData.item || '',
        price: parseFloat(serviceOptionData.price) || 0,
        measurement: serviceOptionData.measurement || 'None'
      };

      let updatedOptions;
      if (serviceOptionData.index !== undefined) {
        // Editing existing option
        updatedOptions = [...serviceOptions];
        updatedOptions[serviceOptionData.index] = sanitizedOption;
      } else {
        // Adding new option
        updatedOptions = [...serviceOptions, sanitizedOption];
      }

      setServiceOptions(updatedOptions);

      const { getUserAccountId } = await import('../utils/accountUtils');
      const { accountId, error: accountIdError } = await getUserAccountId();
      if (accountIdError || !accountId) throw new Error(accountIdError || 'Account ID not found');

      // Save to database
      const { error } = await supabase
        .from('curbside_settings')
        .update({ 
          service_options: updatedOptions,
          updated_at: new Date().toISOString()
        })
        .eq('account_id', accountId);

      if (error) throw error;

      toast.success('Service option saved successfully');
      setEditingItem(null);
      setEditingType(null);
    } catch (err) {
      console.error('Error saving service option:', err);
      toast.error('Failed to save service option');
    }
  };

  // Auto-save service options when changed
  const updateServiceOption = async (index, field, value) => {
    try {
      const updated = [...serviceOptions];
      if (field === 'price') {
        updated[index] = { ...updated[index], [field]: parseFloat(value) || 0 };
      } else {
        updated[index] = { ...updated[index], [field]: value };
      }
      setServiceOptions(updated);

      const { getUserAccountId } = await import('../utils/accountUtils');
      const { accountId, error: accountIdError } = await getUserAccountId();
      if (accountIdError || !accountId) return;

      // Auto-save to database
      const { error } = await supabase
        .from('curbside_settings')
        .update({ 
          service_options: updated,
          updated_at: new Date().toISOString()
        })
        .eq('account_id', accountId);

      if (error) throw error;
    } catch (err) {
      console.error('Error updating service option:', err);
      toast.error('Failed to update service option');
    }
  };

  // Remove service option and auto-save
  const removeServiceOption = async (index) => {
    try {
      const updated = serviceOptions.filter((_, i) => i !== index);
      setServiceOptions(updated);

      const { getUserAccountId } = await import('../utils/accountUtils');
      const { accountId, error: accountIdError } = await getUserAccountId();
      if (accountIdError || !accountId) return;

      // Auto-save to database
      const { error } = await supabase
        .from('curbside_settings')
        .update({ 
          service_options: updated,
          updated_at: new Date().toISOString()
        })
        .eq('account_id', accountId);

      if (error) throw error;
    } catch (err) {
      console.error('Error removing service option:', err);
      toast.error('Failed to remove service option');
    }
  };

  // Add new service option and auto-save
  const addServiceOption = async () => {
    try {
      const newOption = { item: '', price: 0, measurement: 'None' };
      const updated = [...serviceOptions, newOption];
      setServiceOptions(updated);

      if (!userData) return;

      // Auto-save to database
      const { error } = await supabase
        .from('curbside_settings')
        .update({ 
          service_options: updated,
          updated_at: new Date().toISOString()
        })
        .eq('account_id', userData.id);

      if (error) throw error;
    } catch (err) {
      console.error('Error adding service option:', err);
      toast.error('Failed to add service option');
    }
  };

  // Service options CRUD
  const saveServiceOptions = async () => {
    try {
      setLoading(true);
      
      // Sanitize service options data
      const sanitizedOptions = serviceOptions.map(option => ({
        item: option.item || '',
        price: parseFloat(option.price) || 0,
        measurement: option.measurement || 'None'
      }));
      
      const { error } = await supabase
        .from('curbside_settings')
        .update({ service_options: sanitizedOptions })
        .eq('account_id', userData.id);

      if (error) throw error;
      
      toast.success('Service options saved successfully!');
      await fetchData();
    } catch (error) {
      console.error('Error saving service options:', error);
      toast.error('Failed to save service options');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <motion.div 
        className="flex items-center justify-center py-16"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="w-8 h-8 border-2 border-gray-900 border-t-transparent rounded-full mx-auto mb-4"
          />
          <p className="text-sm text-gray-600">Loading settings...</p>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/30">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <motion.div 
          className="space-y-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {integrationStatus === 'complete' ? (
            <div className="space-y-6">
              {/* Service Types */}
              <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm">
                <CardHeader className="pb-4 pt-6 px-6">
                  <CardTitle className="flex items-center space-x-2 text-lg">
                    <div className="p-2 bg-gray-100 rounded-lg">
                      <Stack className="w-4 h-4 text-gray-700" />
                    </div>
                    <span>Service Types</span>
                  </CardTitle>
                  <CardDescription className="text-sm text-gray-600 mt-1">
                    Configure which services your business offers
                  </CardDescription>
                </CardHeader>
                <CardContent className="px-6 pb-6">
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                      <input
                        type="radio"
                        id="laundry"
                        name="serviceTypes"
                        value="laundry"
                        checked={serviceTypes === 'laundry'}
                        onChange={(e) => handleServiceTypesChange(e.target.value)}
                        className="h-4 w-4 text-gray-900 border-gray-300 focus:ring-gray-900"
                      />
                      <label htmlFor="laundry" className="flex items-center text-sm text-gray-700 font-medium flex-1 cursor-pointer">
                        <ShirtFolded className="w-4 h-4 mr-2 text-gray-600" />
                        Wash & Fold Only
                      </label>
                    </div>
                    <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                      <input
                        type="radio"
                        id="dry_cleaning"
                        name="serviceTypes"
                        value="dry_cleaning"
                        checked={serviceTypes === 'dry_cleaning'}
                        onChange={(e) => handleServiceTypesChange(e.target.value)}
                        className="h-4 w-4 text-gray-900 border-gray-300 focus:ring-gray-900"
                      />
                      <label htmlFor="dry_cleaning" className="flex items-center text-sm text-gray-700 font-medium flex-1 cursor-pointer">
                        <Sparkle className="w-4 h-4 mr-2 text-gray-600" />
                        Dry Cleaning Only
                      </label>
                    </div>
                    <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                      <input
                        type="radio"
                        id="both"
                        name="serviceTypes"
                        value="both"
                        checked={serviceTypes === 'both'}
                        onChange={(e) => handleServiceTypesChange(e.target.value)}
                        className="h-4 w-4 text-gray-900 border-gray-300 focus:ring-gray-900"
                      />
                      <label htmlFor="both" className="flex items-center text-sm text-gray-700 font-medium flex-1 cursor-pointer">
                        <Stack className="w-4 h-4 mr-2 text-gray-600" />
                        Both Services
                      </label>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Routes */}
              <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm">
                <CardHeader className="pb-4 pt-6 px-6">
                  <div>
                    <CardTitle className="flex items-center space-x-2 text-lg">
                      <div className="p-2 bg-gray-100 rounded-lg">
                        <MapPin className="w-4 h-4 text-gray-700" />
                      </div>
                      <span>Route Settings</span>
                    </CardTitle>
                    <CardDescription className="text-sm text-gray-600 mt-1">
                      Manage delivery routes and turnaround times
                    </CardDescription>
                  </div>
                  <Button
                    onClick={() => {
                      setEditingItem({
                        route_name: '',
                        wash_and_fold_turnaround: '',
                        dry_cleaning_turnaround: '',
                        days: [],
                        times: [''],
                        special_weekend_hours: null
                      });
                      setEditingType('route');
                    }}
                    className="h-10 px-4 bg-gray-900 hover:bg-gray-800 rounded-lg text-sm font-medium"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Route
                  </Button>
                </CardHeader>
                <CardContent className="px-6 pb-6">
                  {routes.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                        <MapPin className="w-6 h-6 text-gray-400" />
                      </div>
                      <h4 className="text-base font-medium text-gray-900 mb-2">No routes configured</h4>
                      <p className="text-sm text-gray-600 max-w-sm mx-auto">
                        Add delivery routes to manage turnaround times
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {routes.map((route) => (
                        <div key={route.id} className="bg-gray-50/50 p-4 rounded-lg border border-gray-200 hover:shadow-sm transition-all duration-200">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-semibold text-gray-900 text-base">{route.route_name}</h4>
                              <p className="text-sm text-gray-600 mt-1">
                                Wash & Fold: {route.wash_and_fold_turnaround || 'N/A'} | 
                                Dry Cleaning: {route.dry_cleaning_turnaround || 'N/A'}
                              </p>
                            </div>
                            <div className="flex space-x-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setEditingItem(route);
                                  setEditingType('route');
                                }}
                                className="h-8 px-3 border border-gray-200 hover:border-gray-300 rounded-md"
                              >
                                <PencilSimple className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => deleteRoute(route.id)}
                                className="h-8 px-3 text-red-600 border border-red-200 hover:border-red-300 hover:bg-red-50 rounded-md"
                              >
                                <Trash className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Zones */}
              <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm">
                <CardHeader className="pb-4 pt-6 px-6">
                  <div>
                    <CardTitle className="flex items-center space-x-2 text-lg">
                      <div className="p-2 bg-gray-100 rounded-lg">
                        <MapPin className="w-4 h-4 text-gray-700" />
                      </div>
                      <span>Zone Settings</span>
                    </CardTitle>
                    <CardDescription className="text-sm text-gray-600 mt-1">
                      Configure pricing zones and order minimums
                    </CardDescription>
                  </div>
                  <Button
                    onClick={() => {
                      setEditingItem({
                        zone_name: '',
                        price_per_pound_laundry: '',
                        price_add_per_item: '',
                        price_add_dry_cleaning: '',
                        order_minimum: '',
                        service_areas: [],
                        route_id: null
                      });
                      setEditingType('zone');
                    }}
                    className="h-10 px-4 bg-gray-900 hover:bg-gray-800 rounded-lg text-sm font-medium"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Zone
                  </Button>
                </CardHeader>
                <CardContent className="px-6 pb-6">
                  {zones.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                        <MapPin className="w-6 h-6 text-gray-400" />
                      </div>
                      <h4 className="text-base font-medium text-gray-900 mb-2">No zones configured</h4>
                      <p className="text-sm text-gray-600 max-w-sm mx-auto">
                        Create pricing zones for different service areas
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {zones.map((zone) => (
                        <div key={zone.id} className="bg-gray-50/50 p-4 rounded-lg border border-gray-200 hover:shadow-sm transition-all duration-200">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-semibold text-gray-900 text-base">{zone.zone_name}</h4>
                              <p className="text-sm text-gray-600 mt-1">
                                ${zone.price_per_pound_laundry || 'N/A'}/lb | 
                                Min: ${zone.order_minimum || 'N/A'} | 
                                Route: {routes.find(r => r.id === zone.route_id)?.route_name || 'None'}
                              </p>
                            </div>
                            <div className="flex space-x-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setEditingItem(zone);
                                  setEditingType('zone');
                                }}
                                className="h-8 px-3 border border-gray-200 hover:border-gray-300 rounded-md"
                              >
                                <PencilSimple className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => deleteZone(zone.id)}
                                className="h-8 px-3 text-red-600 border border-red-200 hover:border-red-300 hover:bg-red-50 rounded-md"
                              >
                                <Trash className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Service Options */}
              <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm">
                <CardHeader className="pb-4 pt-6 px-6">
                  <div>
                    <CardTitle className="flex items-center space-x-2 text-lg">
                      <div className="p-2 bg-gray-100 rounded-lg">
                        <Gear className="w-4 h-4 text-gray-700" />
                      </div>
                      <span>Service Options</span>
                    </CardTitle>
                    <CardDescription className="text-sm text-gray-600 mt-1">
                      Configure additional services customers can request (auto-saves on change)
                    </CardDescription>
                  </div>
                  <Button
                    onClick={() => {
                      setEditingItem({
                        item: '',
                        price: '',
                        measurement: 'None'
                      });
                      setEditingType('service_option');
                    }}
                    className="h-10 px-4 bg-gray-900 hover:bg-gray-800 rounded-lg text-sm font-medium"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Service Option
                  </Button>
                </CardHeader>
                <CardContent className="px-6 pb-6">
                  <div className="space-y-4">
                    {serviceOptions.length === 0 ? (
                      <div className="text-center py-12 text-gray-500">
                        <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                          <Gear className="w-6 h-6 text-gray-400" />
                        </div>
                        <h4 className="text-base font-medium text-gray-900 mb-2">No service options added</h4>
                        <p className="text-sm text-gray-600 max-w-sm mx-auto">
                          Add extra services customers can request
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {serviceOptions.map((option, index) => (
                          <div key={index} className="bg-gray-50/50 p-4 rounded-lg border border-gray-200 hover:shadow-sm transition-all duration-200">
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className="font-semibold text-gray-900 text-base">{option.item || 'Untitled Service'}</h4>
                                <p className="text-sm text-gray-600 mt-1">
                                  ${option.price?.toFixed(2) || '0.00'} • {option.measurement || 'None'}
                                </p>
                              </div>
                              <div className="flex space-x-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setEditingItem({...option, index});
                                    setEditingType('service_option');
                                  }}
                                  className="h-8 px-3 border border-gray-200 hover:border-gray-300 rounded-md"
                                >
                                  <PencilSimple className="w-3 h-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={async () => {
                                    try {
                                      const updated = serviceOptions.filter((_, i) => i !== index);
                                      setServiceOptions(updated);

                                      if (userData) {
                                        const { error } = await supabase
                                          .from('curbside_settings')
                                          .update({ 
                                            service_options: updated,
                                            updated_at: new Date().toISOString()
                                          })
                                          .eq('account_id', userData.id);

                                        if (error) throw error;
                                        toast.success('Service option deleted successfully');
                                      }
                                    } catch (err) {
                                      console.error('Error deleting service option:', err);
                                      toast.error('Failed to delete service option');
                                    }
                                  }}
                                  className="h-8 px-3 text-red-600 border border-red-200 hover:border-red-300 hover:bg-red-50 rounded-md"
                                >
                                  <Trash className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Main Setup Form - No tabs when integration not complete */}
              <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm">
                <CardHeader className="pb-6 pt-6 px-6">
                  <CardTitle className="flex items-center space-x-2 text-xl">
                    <div className="p-2 bg-gray-100 rounded-xl">
                      <Truck className="w-5 h-5 text-gray-700" />
                    </div>
                    <span>Curbside Integration Setup</span>
                  </CardTitle>
                  <CardDescription className="text-sm text-gray-600 mt-2">
                    Connect your Curbside account to enable AI phone support
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6 px-6 pb-6">
                  {/* URL Input */}
                  <div className="space-y-3">
                    <Label htmlFor="url" className="text-sm font-medium text-gray-900">Your Curbside Subdomain *</Label>
                    <Input
                      id="url"
                      value={url}
                      onChange={(e) => handleInputChange('url', e.target.value)}
                      placeholder="washworks"
                      className={`h-11 text-base border-2 rounded-lg transition-all duration-200 ${urlError ? 'border-orange-300 focus:border-orange-500' : 'border-gray-200 focus:border-gray-900'}`}
                    />
                    {urlError && (
                      <div className="text-orange-600 text-sm bg-orange-50 border border-orange-200 rounded-lg p-3">
                        <div className="flex items-start space-x-2">
                          <Warning className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="font-medium">{urlError.message}</p>
                            {urlError.suggestion && (
                              <button
                                onClick={() => applySuggestion(urlError.suggestion)}
                                className="mt-2 text-sm bg-orange-100 hover:bg-orange-200 px-3 py-1.5 rounded-md transition-colors font-medium"
                              >
                                Use "{urlError.suggestion}" instead
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    {!urlError && url && validateSubdomain(url) === null && (
                      <div className="flex items-center space-x-2 text-green-600 text-sm bg-green-50 border border-green-200 rounded-lg p-3">
                        <Check className="w-4 h-4" />
                        <span className="font-medium">Looks good!</span>
                      </div>
                    )}
                    <p className="text-gray-500 text-xs leading-relaxed">
                      Just your subdomain - for example, if your URL is https://washworks-pds.curbsidelaundries.com, enter "washworks"
                    </p>
                  </div>

                  {/* Bella Created Checkbox */}
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <input
                        type="checkbox"
                        id="bellaCreated"
                        checked={bellaCreated}
                        onChange={(e) => handleInputChange('bellaCreated', e.target.checked)}
                        className="h-4 w-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
                      />
                      <Label htmlFor="bellaCreated" className="text-sm font-medium flex-1">
                        I've created Bella as an employee in Curbside *
                      </Label>
                    </div>
                    <p className="text-gray-500 text-xs ml-1">
                      Need help? Click the setup instructions button below.
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-3 pt-4">
                    <Button
                      onClick={() => setShowSetupInstructions(true)}
                      variant="outline"
                      className="flex-1 h-11 text-sm font-medium border-2 border-gray-200 hover:border-gray-300 rounded-lg"
                    >
                      <Info className="w-4 h-4 mr-2" />
                      Setup Instructions
                    </Button>
                    <Button
                      onClick={handleSetupCurbside}
                      disabled={!formIsValid || loading}
                      className="flex-1 h-11 text-sm font-medium bg-gray-900 hover:bg-gray-800 rounded-lg"
                    >
                      {loading ? (
                        <CircleNotch className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <ArrowSquareOut className="w-4 h-4 mr-2" />
                      )}
                      Connect Integration
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Edit Modal */}
          {editingItem && (
            <EditModal
              item={editingItem}
              type={editingType}
              routes={routes}
              onSave={editingType === 'route' ? saveRoute : editingType === 'zone' ? saveZone : saveServiceOption}
              onCancel={() => {
                setEditingItem(null);
                setEditingType(null);
              }}
            />
          )}

          {/* Modern Step-by-Step Setup Instructions */}
          {showSetupInstructions && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[10001] p-4"
              style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.98, opacity: 0, y: 10 }}
                transition={{ type: "spring", damping: 30, stiffness: 300, duration: 0.4 }}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
                onClick={e => e.stopPropagation()}
              >
                {/* Header */}
                <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <motion.div 
                      className="p-2 bg-gray-100 rounded-xl"
                      whileHover={{ scale: 1.05 }}
                    >
                      <Buildings className="w-5 h-5 text-gray-700" />
                    </motion.div>
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">Setup Bella in Curbside</h2>
                      <p className="text-sm text-gray-500 mt-0.5">
                        Step {currentStep} of {totalSteps} • This takes about 2 minutes
                      </p>
                    </div>
                  </div>
                  
                  <motion.button
                    whileHover={{ scale: 1.05, backgroundColor: '#f3f4f6' }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      setShowSetupInstructions(false);
                      setCurrentStep(1);
                    }}
                    className="rounded-xl h-10 w-10 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </motion.button>
                </div>

                {/* Progress Bar */}
                <div className="px-6 py-3 bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-600">Progress</span>
                    <span className="text-xs text-gray-500">{Math.round((currentStep / totalSteps) * 100)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <motion.div
                      className="bg-black h-2 rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${(currentStep / totalSteps) * 100}%` }}
                      transition={{ duration: 0.3, ease: "easeOut" }}
                    />
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                  <AnimatePresence mode="wait">
                    {currentStep === 1 && (
                      <motion.div
                        key="step1"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.3 }}
                        className="space-y-8"
                      >
                        <div className="text-center">
                          <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                            Open Your Curbside Admin
                          </h3>
                          <p className="text-gray-600 text-base max-w-2xl mx-auto leading-relaxed">
                            First, we need to access your Curbside admin area where you manage employees
                          </p>
                        </div>

                        <div className="bg-gray-50 rounded-2xl p-6">
                          <h4 className="text-base font-medium text-gray-900 mb-4">
                            Click the link below to open your admin area:
                          </h4>
                          <div 
                            onClick={() => window.open(`https://${url || 'your-subdomain'}-pds.curbsidelaundries.com/User`, '_blank')}
                            className="bg-white rounded-xl p-5 border border-gray-200 cursor-pointer hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 group"
                          >
                            <div className="flex items-center justify-between">
                              <code className="text-blue-600 text-base font-mono break-all flex-1 pr-4">
                                https://{url || 'your-subdomain'}-pds.curbsidelaundries.com/User
                              </code>
                              <ArrowSquareOut className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors flex-shrink-0" />
                            </div>
                            <p className="text-sm text-gray-500 mt-2 font-medium">
                              Click to open in new tab
                            </p>
                          </div>
                          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                            <div className="flex items-start space-x-3">
                              <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                              <div>
                                <p className="text-sm text-blue-800 font-medium">This is your admin area</p>
                                <p className="text-sm text-blue-700 mt-1">
                                  Log in with your normal Curbside username and password (not Bella's!)
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Curbside admin login screenshot */}
                        <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-sm">
                          <img 
                            src="https://storage.thesavants.ai/storage/v1/object/public/Pictures//Screenshot%202025-06-28%20at%206.32.57%20PM.png"
                            alt="Curbside admin login page"
                            className="w-full h-auto"
                          />
                        </div>
                      </motion.div>
                    )}

                    {currentStep === 2 && (
                      <motion.div
                        key="step2"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.3 }}
                        className="space-y-6"
                      >
                        <div className="text-center">
                          <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                            Add Bella as Employee
                          </h3>
                          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                            Now we'll create Bella Laundromatic as a new employee in your system
                          </p>
                        </div>

                        <div className="bg-gray-50 rounded-2xl p-6">
                          <h4 className="font-semibold text-gray-900 mb-4">
                            Click the "+ New Employee" button and enter exactly:
                          </h4>

                          {/* Responsive grid - 2x3 on desktop, stacked on mobile */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            {/* First Name */}
                            <div className="bg-white rounded-xl p-4 border border-gray-200">
                              <div className="flex items-center justify-between mb-2">
                                <label className="text-sm font-medium text-gray-500 uppercase tracking-wide">First Name</label>
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText('Bella');
                                    toast.success('Copied "Bella" to clipboard');
                                  }}
                                  className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                              </div>
                              <div className="text-lg font-semibold text-gray-900">Bella</div>
                            </div>

                            {/* Last Name */}
                            <div className="bg-white rounded-xl p-4 border border-gray-200">
                              <div className="flex items-center justify-between mb-2">
                                <label className="text-sm font-medium text-gray-500 uppercase tracking-wide">Last Name</label>
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText('Laundromatic');
                                    toast.success('Copied "Laundromatic" to clipboard');
                                  }}
                                  className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                              </div>
                              <div className="text-lg font-semibold text-gray-900">Laundromatic</div>
                            </div>

                            {/* Password */}
                            <div className="bg-white rounded-xl p-4 border border-gray-200">
                              <div className="flex items-center justify-between mb-2">
                                <label className="text-sm font-medium text-gray-500 uppercase tracking-wide">Password</label>
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText('BellaAI1!');
                                    toast.success('Copied password to clipboard');
                                  }}
                                  className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                              </div>
                              <div className="text-lg font-semibold text-gray-900">BellaAI1!</div>
                            </div>

                            {/* Password Again */}
                            <div className="bg-white rounded-xl p-4 border border-gray-200">
                              <div className="flex items-center justify-between mb-2">
                                <label className="text-sm font-medium text-gray-500 uppercase tracking-wide">Password Again</label>
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText('BellaAI1!');
                                    toast.success('Copied password to clipboard');
                                  }}
                                  className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                              </div>
                              <div className="text-lg font-semibold text-gray-900">BellaAI1!</div>
                            </div>

                            {/* Email */}
                            <div className="bg-white rounded-xl p-4 border border-gray-200">
                              <div className="flex items-center justify-between mb-2">
                                <label className="text-sm font-medium text-gray-500 uppercase tracking-wide">Email</label>
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText('bella@thesavants.ai');
                                    toast.success('Copied email to clipboard');
                                  }}
                                  className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                              </div>
                              <div className="text-lg font-semibold text-gray-900">bella@thesavants.ai</div>
                            </div>

                            {/* Phone Number */}
                            <div className="bg-white rounded-xl p-4 border border-gray-200">
                              <div className="flex items-center justify-between mb-2">
                                <label className="text-sm font-medium text-gray-500 uppercase tracking-wide">Phone Number</label>
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(getRawPhoneDigits(aiPhoneNumber));
                                    toast.success('Copied phone number to clipboard');
                                  }}
                                  className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                              </div>
                              <div className="text-lg font-semibold text-gray-900">{formatPhoneNumber(aiPhoneNumber)}</div>
                            </div>
                          </div>

                          <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                            <div className="flex items-start space-x-3">
                              <Warning className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                              <div>
                                <p className="text-sm text-amber-800 font-semibold">Copy these exactly!</p>
                                <p className="text-xs text-amber-700 mt-1">
                                  Don't change anything - these credentials let our AI connect to your system
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Employee creation form screenshot */}
                        <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-sm">
                          <img 
                            src="https://storage.thesavants.ai/storage/v1/object/public/Pictures//Screenshot%202025-06-28%20at%206.30.15%20PM.png"
                            alt="Employee creation form"
                            className="w-full h-auto"
                          />
                        </div>
                      </motion.div>
                    )}

                    {currentStep === 3 && (
                      <motion.div
                        key="step3"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.3 }}
                        className="space-y-6"
                      >
                        <div className="text-center">
                          <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                            Set Bella's Permissions
                          </h3>
                          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                            Give Bella the right access levels so she can help your customers
                          </p>
                        </div>

                        {/* Permission settings screenshot */}
                        <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-sm">
                          <img 
                            src="https://storage.thesavants.ai/storage/v1/object/public/Pictures//Screenshot%202025-06-28%20at%206.31.09%20PM.png"
                            alt="Permission settings page"
                            className="w-full h-auto"
                          />
                        </div>

                        <div className="bg-gray-50 rounded-2xl p-6">
                          <p className="text-base text-gray-700 text-center">
                            Set up Bella's permissions exactly as shown in the image above.
                          </p>
                        </div>
                      </motion.div>
                    )}

                    {currentStep === 4 && (
                      <motion.div
                        key="step4"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.3 }}
                        className="space-y-6"
                      >
                        <div className="text-center">
                          <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                            You're All Set! 🎉
                          </h3>
                          <p className="text-gray-600 text-base max-w-2xl mx-auto leading-relaxed">
                            Bella is now ready to help your customers through your AI phone system
                          </p>
                        </div>

                        <div className="bg-gray-50 rounded-2xl p-6">
                          <h4 className="font-semibold text-gray-900 mb-4">
                            Ready to connect?
                          </h4>

                          <p className="text-gray-600 text-sm mb-6">
                            Once you've saved Bella as an employee in your Curbside system, click the button below to confirm and automatically connect your integration.
                          </p>

                          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-xl">
                            <div className="flex items-start space-x-3">
                              <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                              <div>
                                <p className="text-sm text-green-800 font-medium">After connecting, Bella will be able to:</p>
                                <ul className="text-xs text-green-700 mt-1 space-y-1">
                                  <li>• Check order statuses for customers</li>
                                  <li>• Update delivery information</li>
                                  <li>• Handle customer service requests</li>
                                  <li>• Answer questions about your services</li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Navigation Footer */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-between items-center">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
                    disabled={currentStep === 1}
                    className="px-6 py-2.5 rounded-xl border border-gray-200 text-gray-700 bg-white 
                      hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-medium
                      transition-colors"
                  >
                    ← Previous
                  </motion.button>

                  <div className="flex space-x-2">
                    {Array.from({ length: totalSteps }, (_, i) => (
                      <motion.div
                        key={i}
                        className={`w-2 h-2 rounded-full transition-colors ${
                          i + 1 <= currentStep ? 'bg-black' : 'bg-gray-200'
                        }`}
                        animate={{ scale: i + 1 === currentStep ? 1.2 : 1 }}
                        transition={{ duration: 0.2 }}
                      />
                    ))}
                  </div>

                  {currentStep < totalSteps ? (
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setCurrentStep(Math.min(totalSteps, currentStep + 1))}
                      className="px-6 py-2.5 rounded-xl bg-black text-white font-medium
                        hover:bg-gray-800 transition-colors"
                    >
                      Next →
                    </motion.button>
                  ) : (
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleBellaCreatedConfirmation}
                      className="px-6 py-2.5 rounded-xl bg-green-600 text-white font-medium
                        hover:bg-green-700 transition-colors"
                    >
                      <span className="hidden sm:inline">I've Created Bella as an Employee ✓</span>
                      <span className="sm:hidden">Bella Created ✓</span>
                    </motion.button>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

// Edit Modal Component
const EditModal = ({ item, type, routes, onSave, onCancel }) => {
  const [formData, setFormData] = useState(item);
  const [loading, setLoading] = useState(false);

  // Update formData when item changes (for proper route_id initialization)
  useEffect(() => {
    setFormData(item);
  }, [item]);

  // Helper functions for user-friendly JSONB field management
  const addDay = () => {
    const days = formData.days || [];
    setFormData({ ...formData, days: [...days, ''] });
  };

  const updateDay = (index, value) => {
    const days = [...(formData.days || [])];
    days[index] = value;
    setFormData({ ...formData, days });
  };

  const removeDay = (index) => {
    const days = [...(formData.days || [])];
    days.splice(index, 1);
    setFormData({ ...formData, days });
  };

  const addTimeSlot = () => {
    const times = formData.times || [];
    setFormData({ ...formData, times: [...times, ''] });
  };

  const updateTimeSlot = (index, value) => {
    const times = [...(formData.times || [])];
    times[index] = value;
    setFormData({ ...formData, times });
  };

  const removeTimeSlot = (index) => {
    const times = [...(formData.times || [])];
    times.splice(index, 1);
    setFormData({ ...formData, times });
  };

  const updateWeekendHours = (day, value) => {
    const weekendHours = { ...(formData.special_weekend_hours || {}) };
    if (value.trim() === '') {
      delete weekendHours[day];
    } else {
      weekendHours[day] = value;
    }
    setFormData({ ...formData, special_weekend_hours: weekendHours });
  };

  const addServiceArea = () => {
    const serviceAreas = Array.isArray(formData.service_areas) ? formData.service_areas : [];
    setFormData({ 
      ...formData, 
      service_areas: [...serviceAreas, ''] 
    });
  };

  const updateServiceArea = (index, value) => {
    const serviceAreas = Array.isArray(formData.service_areas) ? [...formData.service_areas] : [];
    serviceAreas[index] = value;
    setFormData({ 
      ...formData, 
      service_areas: serviceAreas 
    });
  };

  const removeServiceArea = (index) => {
    const serviceAreas = Array.isArray(formData.service_areas) ? [...formData.service_areas] : [];
    serviceAreas.splice(index, 1);
    setFormData({ 
      ...formData, 
      service_areas: serviceAreas 
    });
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSave(formData);
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[99999] p-4" 
      style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0, 
        width: '100vw', 
        height: '100vh',
        minHeight: '100vh',
        minWidth: '100vw'
      }}
    >
      <motion.div 
        className="bg-white rounded-2xl w-full max-w-2xl shadow-xl border border-gray-100 overflow-hidden max-h-[90vh] flex flex-col"
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ type: "spring", damping: 20, stiffness: 300 }}
      >
        <div className="p-6 border-b border-gray-100 flex-shrink-0">
          <h3 className="text-lg font-medium text-gray-900">
            {type === 'route' ? 'Route Settings' : type === 'zone' ? 'Zone Settings' : 'Service Option'}
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            {type === 'route' 
              ? 'Configure delivery route and turnaround times' 
              : type === 'zone' 
              ? 'Set up pricing zone and minimum order requirements'
              : 'Configure additional service option'
            }
          </p>
        </div>
        
        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {type === 'route' ? (
                <>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">Route Name</Label>
                    <Input
                      value={formData.route_name || ''}
                      onChange={(e) => setFormData({ ...formData, route_name: e.target.value })}
                      placeholder="Enter route name"
                      className="rounded-xl border-gray-200 focus:border-gray-900"
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">Wash & Fold Turnaround</Label>
                      <Input
                        type="text"
                        value={formData.wash_and_fold_turnaround || ''}
                        onChange={(e) => setFormData({ ...formData, wash_and_fold_turnaround: e.target.value })}
                        placeholder="e.g., 2-3 days"
                        className="rounded-xl border-gray-200 focus:border-gray-900"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">Dry Cleaning Turnaround</Label>
                      <Input
                        type="text"
                        value={formData.dry_cleaning_turnaround || ''}
                        onChange={(e) => setFormData({ ...formData, dry_cleaning_turnaround: e.target.value })}
                        placeholder="e.g., 3-5 days"
                        className="rounded-xl border-gray-200 focus:border-gray-900"
                      />
                    </div>
                  </div>
                  
                  {/* Days - User Friendly */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium text-gray-700">Service Days</Label>
                      <Button
                        type="button"
                        onClick={addDay}
                        size="sm"
                        className="bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Day
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {(formData.days || []).map((day, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <select
                            value={day}
                            onChange={(e) => updateDay(index, e.target.value)}
                            className="flex-1 p-2 border border-gray-200 rounded-lg text-sm"
                          >
                            <option value="">Select a day</option>
                            <option value="Monday">Monday</option>
                            <option value="Tuesday">Tuesday</option>
                            <option value="Wednesday">Wednesday</option>
                            <option value="Thursday">Thursday</option>
                            <option value="Friday">Friday</option>
                            <option value="Saturday">Saturday</option>
                            <option value="Sunday">Sunday</option>
                          </select>
                          <Button
                            type="button"
                            onClick={() => removeDay(index)}
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      {(formData.days || []).length === 0 && (
                        <p className="text-sm text-gray-500 italic">No service days added yet</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Times - User Friendly */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium text-gray-700">Service Hours</Label>
                      <Button
                        type="button"
                        onClick={addTimeSlot}
                        size="sm"
                        className="bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Hours
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {(formData.times || []).map((time, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <Input
                            value={time}
                            onChange={(e) => updateTimeSlot(index, e.target.value)}
                            placeholder="e.g., 9:00 AM - 5:00 PM"
                            className="flex-1 rounded-lg border-gray-200"
                          />
                          <Button
                            type="button"
                            onClick={() => removeTimeSlot(index)}
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      {(formData.times || []).length === 0 && (
                        <p className="text-sm text-gray-500 italic">No service hours added yet</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Weekend Hours - User Friendly */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium text-gray-700">Special Weekend Hours</Label>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs text-gray-600">Saturday</Label>
                        <Input
                          value={(formData.special_weekend_hours || {}).saturday || ''}
                          onChange={(e) => updateWeekendHours('saturday', e.target.value)}
                          placeholder="e.g., 9:00 AM - 3:00 PM or Closed"
                          className="rounded-lg border-gray-200"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-gray-600">Sunday</Label>
                        <Input
                          value={(formData.special_weekend_hours || {}).sunday || ''}
                          onChange={(e) => updateWeekendHours('sunday', e.target.value)}
                          placeholder="e.g., 10:00 AM - 2:00 PM or Closed"
                          className="rounded-lg border-gray-200"
                        />
                      </div>
                    </div>
                  </div>
                </>
              ) : type === 'zone' ? (
                <>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">Zone Name</Label>
                    <Input
                      value={formData.zone_name || ''}
                      onChange={(e) => setFormData({ ...formData, zone_name: e.target.value })}
                      placeholder="Enter zone name"
                      className="rounded-xl border-gray-200 focus:border-gray-900"
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">Price per Pound</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-500">$</span>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.price_per_pound_laundry || ''}
                          onChange={(e) => setFormData({ ...formData, price_per_pound_laundry: e.target.value })}
                          placeholder="0.00"
                          className="rounded-xl border-gray-200 focus:border-gray-900 pl-8"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">Order Minimum</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-500">$</span>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.order_minimum || ''}
                          onChange={(e) => setFormData({ ...formData, order_minimum: e.target.value })}
                          placeholder="0.00"
                          className="rounded-xl border-gray-200 focus:border-gray-900 pl-8"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">Extra Item Fee</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-500">$</span>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.price_add_per_item || ''}
                          onChange={(e) => setFormData({ ...formData, price_add_per_item: e.target.value })}
                          placeholder="0.00"
                          className="rounded-xl border-gray-200 focus:border-gray-900 pl-8"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">Dry Cleaning Fee</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-500">$</span>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.price_add_dry_cleaning || ''}
                          onChange={(e) => setFormData({ ...formData, price_add_dry_cleaning: e.target.value })}
                          placeholder="0.00"
                          className="rounded-xl border-gray-200 focus:border-gray-900 pl-8"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Service Areas - Simple Array Structure */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium text-gray-700">Service Areas & ZIP Codes</Label>
                      <Button
                        type="button"
                        onClick={addServiceArea}
                        size="sm"
                        className="bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Area/ZIP
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {(Array.isArray(formData.service_areas) ? formData.service_areas : []).map((area, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <Input
                            value={area}
                            onChange={(e) => updateServiceArea(index, e.target.value)}
                            placeholder="e.g., Downtown, Midtown, 12345, 67890"
                            className="flex-1 rounded-lg border-gray-200"
                          />
                          <Button
                            type="button"
                            onClick={() => removeServiceArea(index)}
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      {(Array.isArray(formData.service_areas) ? formData.service_areas : []).length === 0 && (
                        <p className="text-sm text-gray-500 italic">No service areas or ZIP codes added yet</p>
                      )}
                    </div>
                  </div>
                  
                  {routes.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">Route Assignment</Label>
                      <select
                        className="w-full p-3 border border-gray-200 rounded-xl focus:border-gray-900 focus:ring-1 focus:ring-gray-900 bg-white text-sm"
                        value={formData.route_id || ''}
                        onChange={(e) => {
                          const selectedValue = e.target.value === '' ? null : e.target.value;
                          setFormData({ ...formData, route_id: selectedValue });
                        }}
                      >
                        <option value="">Select a route (optional)</option>
                        {routes.map((route) => (
                          <option key={route.id} value={route.id}>
                            {route.route_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">Service Item</Label>
                    <Input
                      value={formData.item || ''}
                      onChange={(e) => setFormData({ ...formData, item: e.target.value })}
                      placeholder="e.g., Extra Fabric Softener"
                      className="rounded-xl border-gray-200 focus:border-gray-900"
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">Price</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-500">$</span>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.price || ''}
                          onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                          placeholder="0.00"
                          className="rounded-xl border-gray-200 focus:border-gray-900 pl-8"
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">Measurement</Label>
                      <select
                        value={formData.measurement || 'None'}
                        onChange={(e) => setFormData({ ...formData, measurement: e.target.value })}
                        className="w-full p-3 border border-gray-200 rounded-xl focus:border-gray-900 focus:ring-1 focus:ring-gray-900 bg-white text-sm"
                      >
                        <option value="None">None</option>
                        <option value="Per Item">Per Item</option>
                        <option value="Per Load">Per Load</option>
                        <option value="Per Pound">Per Pound</option>
                      </select>
                    </div>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
        
        <div className="p-6 border-t border-gray-100 flex justify-end space-x-3 flex-shrink-0">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="rounded-xl"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="bg-gray-900 hover:bg-gray-800 text-white rounded-xl"
          >
            {loading ? (
              <>
                <Spinner className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </motion.div>
    </div>,
    document.body
  );
};

export default CurbsideIntegration; 